/**
 * Chat Window Component
 * Main chat interface with conversation flow management
 */
import React, { useState, useEffect, useRef } from 'react';
import IdentificationForm from './IdentificationForm';
import LanguageSelector from './LanguageSelector';
import FAQView from './FAQView';
import AIMessageInput from './AIMessageInput';
import MessageList from './MessageList';
import { translations } from '../translations';

const ChatWindow = ({
  isOpen,
  onClose,
  chatState,
  messages,
  user,
  language,
  isLoading,
  isTyping,
  identificationError,
  onUserIdentification,
  onLanguageSelection,
  onFAQSelection,
  onAIMessage,
  onHumanRequest,
  onCancelHuman,
  onGetFAQs
}) => {
  const [faqs, setFaqs] = useState([]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (chatState === 'faq_interaction' && onGetFAQs) {
      loadFAQs();
    }
  }, [chatState]);

  const loadFAQs = async () => {
    try {
      const faqData = await onGetFAQs();
      setFaqs(faqData || []);
    } catch (error) {
      console.error('Failed to load FAQs:', error);
    }
  };

  const t = translations[language] || translations.en;

  /**
   * Determine if a language is RTL (Right-to-Left)
   * RTL languages: Persian (fa), Pashto (ps), Arabic (ar)
   * LTR languages: English (en), Turkish (tr)
   */
  const isRTL = (lang) => {
    return ['fa', 'ps', 'ar'].includes(lang);
  };

  /**
   * Get text direction for a language
   */
  const getTextDirection = (lang) => {
    return isRTL(lang) ? 'rtl' : 'ltr';
  };

  const textDirection = getTextDirection(language);

  return (
    <div className="afgcoin-chatbot-window" dir={textDirection}>
      <div className="afgcoin-chatbot-header">
        <div className="afgcoin-chatbot-header-content">
          <h3 
            dir={textDirection}
            style={{
              textAlign: isRTL(language) ? 'right' : 'left',
              unicodeBidi: 'plaintext'
            }}
          >
            {t.chatbotName}
          </h3>
          <button
            className="afgcoin-chatbot-close"
            onClick={onClose}
            aria-label="Close chat"
          >
            ×
          </button>
        </div>
      </div>

      <div className="afgcoin-chatbot-body">
        {/* CRITICAL: Only show messages when NOT in identification state */}
        {/* During identification, hide all messages to show clean identification form */}
        {chatState !== 'identification' && (
          <MessageList messages={messages || []} language={language} isTyping={isTyping} />
        )}

        {chatState === 'identification' && (
          <IdentificationForm
            onSubmit={onUserIdentification}
            language={language}
            isLoading={isLoading}
            submitError={identificationError}
          />
        )}

        {chatState === 'language_selection' && (
          <LanguageSelector
            onSelect={onLanguageSelection}
            currentLanguage={language}
            isLoading={isLoading}
          />
        )}

        {chatState === 'faq_interaction' && (
          <FAQView
            faqs={faqs}
            onSelect={onFAQSelection}
            language={language}
            isLoading={isLoading}
          />
        )}

        {(chatState === 'ai_fallback' || chatState === 'human_waiting') && (
          <AIMessageInput
            onSend={onAIMessage}
            onRequestHuman={onHumanRequest}
            language={language}
            isLoading={isLoading}
          />
        )}

        {chatState === 'human_waiting' && (
          <div 
            className="afgcoin-chatbot-human-waiting"
            dir={textDirection}
            style={{
              textAlign: isRTL(language) ? 'right' : 'left',
              unicodeBidi: 'plaintext'
            }}
          >
            <p>{t.humanWaiting}</p>
            <div className="afgcoin-chatbot-human-waiting-actions">
              {/* Allow user to return to normal chatbot interaction */}
              <button
                type="button"
                className="afgcoin-chatbot-button-primary"
                onClick={onCancelHuman}
                disabled={isLoading}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word'
                }}
              >
                {t.continueWithChatbot}
              </button>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default ChatWindow;

